import { useMemo } from "react";

import {
  BillingAddonType,
  BillingCycleInterval,
  BillingSubscription,
  BillingTier,
} from "types/generated";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { isStripeEnabled } from "utils/stripe";

import { STARTER_ANNUAL_DISCOUNT, TALK_TO_SALES_LINK } from "../constants";
import { BillingContext } from "../Context";
import { MarketingTileData } from "./types";

const getStarterInfo = (
  billingSubscription?: BillingSubscription,
  addons?: BillingAddonType[] | null,
  isBilledExternally: boolean = false,
  isOctober2026Pricing: boolean = false
): MarketingTileData => {
  if (
    !isBilledExternally &&
    billingSubscription?.billingInterval &&
    billingSubscription.billingInterval === BillingCycleInterval.Monthly
  ) {
    return {
      title: "Switch to Annual",
      subtitle: `Save up to ${STARTER_ANNUAL_DISCOUNT}%`,
      description: `Save up to ${STARTER_ANNUAL_DISCOUNT}% and gain 2 months for free by upgrading to annual Starter plan.`,
      ctaText: "Upgrade",
      ctaTo: "/settings/billing/subscription",
    };
  }

  if (!!billingSubscription && !isBilledExternally && addons && addons.length === 0) {
    return {
      title: "Choose Add-ons",
      subtitle: "Running out of workers or seats?",
      description:
        "Add unlimited users or an additional private worker to your subscription for a seamless workflow!",
      ctaText: "Manage plan",
      ctaTo: "/settings/billing/subscription",
    };
  }

  if (!isOctober2026Pricing) {
    return {
      title: "Switch to Starter Plus",
      subtitle: "Ready to Grow?",
      description:
        "Upgrade to Starter Plus for unlimited users, a private worker, and drift detection to help you automate your infrastructure pipelines.",
      ctaText: "Talk to sales",
      ctaHref: TALK_TO_SALES_LINK,
    };
  }

  return {
    title: "Switch to Business",
    subtitle: "Ready to grow?",
    description:
      "Upgrade to Business for unlimited users, drift detection and workers that can be public or private.",
    ctaText: "Talk to sales",
    ctaHref: TALK_TO_SALES_LINK,
  };
};

const useMarketingTileData = () => {
  const { tier, billedExternally, billingSubscription, isTrial, currentAddons } =
    useTypedContext(BillingContext);
  const { october2026PricingChanges } = useTypedFlags();

  const finalTier = isTrial ? BillingTier.V4Free : tier;
  const shouldTalkToSales = billedExternally || !isStripeEnabled();

  return useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (finalTier) {
      case BillingTier.V4Free:
      case BillingTier.V5Free:
        return {
          title: "Upgrade to Starter Plan",
          subtitle: `Save up to ${STARTER_ANNUAL_DISCOUNT}%`,
          description: `Save up to ${STARTER_ANNUAL_DISCOUNT}% and gain 2 months for free by upgrading to annual Starter plan.`,
          ctaText: shouldTalkToSales ? "Talk to sales" : "Upgrade",
          ctaTo: shouldTalkToSales ? undefined : "/settings/billing/upgrade",
          ctaHref: shouldTalkToSales ? TALK_TO_SALES_LINK : undefined,
        };
      case BillingTier.V4Starter:
      case BillingTier.V5Starter:
        return getStarterInfo(
          billingSubscription,
          currentAddons,
          billedExternally,
          october2026PricingChanges
        );
      case BillingTier.V4StarterPlus:
      case BillingTier.V5StarterPlus:
      case BillingTier.V5StarterPlus_2:
        return {
          title: "Switch to Business",
          subtitle: "Looking for more?",
          description:
            "Switch to Business for more private workers and premium support to take your infrastructure orchestration to the next level.",
          ctaText: "Talk to sales",
          ctaHref: TALK_TO_SALES_LINK,
        };
      case BillingTier.V4Business:
      case BillingTier.V5Business:
        return {
          title: "Switch to Enterprise",
          subtitle: "Outgrowing your plan?",
          description:
            "Upgrade to Enterprise for enhanced security, concurrent VCS connections and advanced audit capabilities for your growing business.",
          ctaText: "Talk to sales",
          ctaHref: TALK_TO_SALES_LINK,
        };
      case BillingTier.V4Enterprise:
      case BillingTier.V5Enterprise:
        return {
          title: "Switch to Enterprise +",
          subtitle: "Outgrowing your plan?",
          description: "Upgrade to Enterprise + for special security, compliance and support.",
          ctaText: "Talk to sales",
          ctaHref: TALK_TO_SALES_LINK,
        };
      case BillingTier.Free:
      case BillingTier.Cloud:
      case BillingTier.Enterprise:
      case BillingTier.V3Free:
      case BillingTier.V3Cloud:
        return {
          title: "Explore New Plans",
          subtitle: "New plans available",
          description:
            "Contact our sales team to learn about our latest plans with improved features and pricing.",
          ctaText: "Contact sales",
          ctaHref: TALK_TO_SALES_LINK,
        };
      default:
        return null;
    }
  }, [
    finalTier,
    shouldTalkToSales,
    billingSubscription,
    currentAddons,
    billedExternally,
    october2026PricingChanges,
  ]);
};

export default useMarketingTileData;
